import { Injectable } from "@angular/core";
import { DbDaoBase } from "../../../../../gyzmo-commons/dao/db/base/db.dao.base";
import { NotImplementedError } from "../../../../../gyzmo-commons/helpers/notImplementedError";
import { AppSqlProvider } from "../../../../../gyzmo-commons/persistence/app.sql.provider";
import { LoggerService } from "../../../../../gyzmo-commons/services/logs/logger.service";
import { Damage } from "../../../../models/inspection/v2/damage.model";
import { AttachmentDbDao } from "../../attachment.db.dao";

@Injectable({
    providedIn: "root",
})
export class DamageDbDaoV2 extends DbDaoBase<Damage> {
    constructor(
        logger: LoggerService,
        private sqlProvider: AppSqlProvider,
        private attachmentDbDao: AttachmentDbDao,
    ) {
        super(logger);
    }

    getByInspectionIdAndZoneId(inspectionId: string, zoneId: string, hydrate: boolean = false): Promise<Damage[]> {
        const selectQuery = `SELECT * FROM ${ Damage.TABLENAME } WHERE inspectionId = ? AND zoneId = ?;`;
        const params = [inspectionId, zoneId];
        return this.sqlProvider.query(selectQuery, params)
            .then(async data => {
                if (data.rows.length <= 0) {
                    return [];
                }

                let hydratationPromises = [];
                const damages: Damage[] = [];
                for (const item of data.rows) {
                    const damage: Damage = this.rowToModel(item);

                    if (hydrate) {
                        hydratationPromises.push(this.attachmentDbDao.getByObjectAndKey("damage", damage.id)
                            .then(value => {
                                damage.attachments = value;
                            }));
                    }

                    damages.push(damage);
                }

                await Promise.all(hydratationPromises);

                return damages;
            })
            .catch(reason => {
                this.logSqlError(reason);
                return null;
            });
    }

    public async createIndexes(): Promise<void> {
        let query = `CREATE INDEX IF NOT EXISTS idx_${ Damage.TABLENAME }_inspectionId ON ${ Damage.TABLENAME }(inspectionId);`;
        await this.sqlProvider.query(query, [])
            .catch(reason => {
                this.logSqlError(reason);
            });

        query = `CREATE INDEX IF NOT EXISTS idx_${ Damage.TABLENAME }_zoneId ON ${ Damage.TABLENAME }(zoneId);`;
        await this.sqlProvider.query(query, [])
            .catch(reason => {
                this.logSqlError(reason);
            });
    }

    public createTable(): Promise<void> {
        const query = `CREATE TABLE IF NOT EXISTS ${ Damage.TABLENAME } (
            id TEXT PRIMARY KEY,
            inspectionId TEXT,
            zoneId TEXT,
            elementId TEXT,
            value TEXT,
            clientResponsibility TEXT,
            xAxis NUMERIC,
            yAxis NUMERIC,
            rowNumber TEXT,
            updateDate TEXT,
            isOld NUMERIC,
            isDeactivate NUMERIC
        );`;
        return this.sqlProvider.query(query, [])
            .then(async () => {
                await this.createIndexes();
            })
            .catch(reason => {
                this.logSqlError(reason);
                return null;
            });
    }

    delete(id: string): Promise<any> {
        const deleteQuery = `DELETE FROM ${ Damage.TABLENAME } WHERE id = ?;`;
        const params = [id];
        return this.sqlProvider.query(deleteQuery, params);
    }

    deleteAll(): Promise<any> {
        const deleteAllQuery = `DELETE FROM ${ Damage.TABLENAME };`;
        return this.sqlProvider.query(deleteAllQuery, []);
    }

    public get(id: string, hydrate: boolean = false): Promise<Damage> {
        throw new NotImplementedError();
    }

    public getTableName(): string {
        return Damage.TABLENAME;
    }

    protected rowToModel(row: any): Damage {
        const damage = new Damage();
        damage.id = row.id;
        damage.inspectionId = row.inspectionId;
        damage.zoneId = row.zoneId;
        damage.elementId = row.elementId;
        damage.value = row.value;
        damage.clientResponsibility = row.clientResponsibility;
        damage.xAxis = row.xAxis;
        damage.yAxis = row.yAxis;
        damage.rowNumber = row.rowNumber;
        damage.updateDate = row.updateDate;
        damage.isOld = row.isOld;
        damage.isDeactivate = row.isDeactivate;
        return damage;
    }

    public save(damage: Damage): Promise<Damage> {
        const promises = damage.attachments.map(attachment => this.attachmentDbDao.save(attachment));
        return Promise.all(promises)
            .then(() => {
                const query = `INSERT OR REPLACE INTO ${ Damage.TABLENAME } (
                    id, inspectionId, zoneId, elementId, value, clientResponsibility,
                    xAxis, yAxis, rowNumber, updateDate, isOld, isDeactivate
                ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?);`;
                const params = [
                    damage.id,
                    damage.inspectionId,
                    damage.zoneId,
                    damage.elementId,
                    damage.value,
                    damage.clientResponsibility,
                    damage.xAxis,
                    damage.yAxis,
                    damage.rowNumber,
                    damage.updateDate,
                    damage.isOld,
                    damage.isDeactivate,
                ];
                return this.sqlProvider.query(query, params)
                    .then(() => {
                        return damage;
                    })
                    .catch(reason => {
                        this.logSqlError(reason);
                        return null;
                    });
            });
    }
}
